(ns app.front.view.occupancy.edit
  (:require [app.domain.execution.model :as execution-model]
            [app.domain.party.model :as party-model]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.system-of-units.toggle :as sou-toggle]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.domain.system-of-units :as sou]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.i18n :as i18n]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; FIXME: The edit mode & signals are never used as the editing happens inline in app.front.view.occupancy.list

(rf/reg-event-fx
  ::update-signal
  (fn [_ [_ execution-id app-id attr value]]
    {:dispatch [::process/signal
                execution-id
                [:process.signal/edit-partial-occupancy
                 {:app-id        app-id
                  (keyword attr) value}]
                {:notify {:success :occupancy/update-success}}]}))


(rf/reg-event-fx
  ::edit-occupancy-application-field
  (fn [{:keys [db]} [_ {:keys [app-id attr value]}]]
    (let [execution-id (get-in db [:view/workspace :execution-id])]
      {:db                (assoc-in db [::edit-occupancy-application app-id (keyword attr)] value)
       :dispatch-debounce {:id      ::edit-app-input-change
                           :timeout 500
                           :action  :dispatch
                           :event   [::update-signal execution-id app-id attr value]}})))

;; partial occupancy application subs and evts

(rf/reg-event-fx
  ::apply-partial-occupancy-success
  ;; `::process/signal` has already applied the db changes carried by the response.
  (fn [{:keys [db]} [_ ws-id _status _resp]]
    {:dispatch [:notifications/add
                :toast
                :success
                :success
                :occupancy/application-submitted]
     :db       (update db ::partial-occupancy-app dissoc ws-id)}))


(defn- resolve-applicants [lang execution user]
  (->> (if (party-model/role-only-has-non-user-parties? execution :role/applicant)
         (party-model/active-parties-for-the-role execution :role/applicant)
         (party-model/parties-for-user user execution :role/applicant))
       (sort-by #(party-model/corporation-or-full-name % lang))))


(rf/reg-event-fx
  ::apply-partial-occupancy
  (fn [{:keys [db]} [_ execution]]
    (let [lang      (i18n/language-query db)
          ws-id     (-> execution :entity/parent :workspace/id)
          data      (get-in db [::partial-occupancy-app ws-id])
          applicant (or (:applicant data)
                        (->> (:session/user db)
                             (resolve-applicants lang execution)
                             first
                             :db/id))]
      {:dispatch [::process/signal
                  execution
                  [:process.signal/apply-partial-occupancy
                   (merge {:applicant applicant}
                          (select-keys data [:area-to-be-occupied
                                             :expected-date-of-occupancy
                                             :description-of-request]))]
                  {:on-success [::apply-partial-occupancy-success ws-id]}]})))


(rf/reg-sub ::applicants
  :<- [:language]
  :<- [::ws-subs/execution]
  :<- [:session/user]
  (fn [[lang execution user] _]
    (resolve-applicants lang execution user)))


(rf/reg-event-db
  ::set-new-application-value
  (fn [db [_ {:keys [on-change attr ws-id value]}]]
    (let [new-db (-> (assoc-in db [::partial-occupancy-app ws-id attr] value)
                     (assoc-in [::partial-occupancy-app ws-id :not-edited?] false))]
      ;; on-change function is present when adding a new application
      ;; function is located in action-parameters and it validates
      ;; that all the fields are filled before submit is available
      (on-change (get-in new-db [::partial-occupancy-app ws-id]))
      new-db)))


(rf/reg-sub ::get-new-application-value
  (fn [db [_ attr ws-id]]
    (-> db ::partial-occupancy-app (get ws-id) attr)))


(rf/reg-sub ::new-applications-by-ws-id
  (fn [db _]
    (-> db ::partial-occupancy-app)))


(rf/reg-sub ::new-application-valid?
  :<- [::ws-subs/workspace-id]
  :<- [::new-applications-by-ws-id]
  :<- [::applicants]
  (fn [[ws-id apps applicants] _]
    (let [ks  (cond-> [:area-to-be-occupied :expected-date-of-occupancy :description-of-request]
                (> (count applicants) 1) (conj :applicant))
          app (get apps ws-id)]
      (every? #(let [v (get app %)]
                 (and v (or (not (string? v))
                            (not (str/blank? v)))))
              ks))))


(defn attr-handler [attr]
  (fn [db [_ workspace-id val]]
    (assoc-in db [::partial-occupancy workspace-id attr] val)))


(rf/reg-event-db
  ::toggle-partial-occupancy
  (attr-handler :enabled?))


(rf/reg-event-db
  ::set-partial-occupancy-instructions
  (attr-handler :instructions))


(rf/reg-event-db ::clear-config-editor
  (fn [db [_ ws-id]]
    (p/dissoc-in db [::partial-occupancy ws-id])))


(rf/reg-sub ::data-in-execution
  :<- [::ws-subs/execution]
  (fn [execution _]
    (p/assoc-if {}
                :enabled? (execution-model/feature? execution :execution.feature/partial-occupation)
                :instructions (:execution/partial-occupation-instructions execution))))


(rf/reg-sub ::data-in-editor
  (fn [db [_ ws-id]]
    (get-in db [::partial-occupancy ws-id])))


(defn area-to-be-occupied-input [{:keys [on-change ws-id value label-text class-name app-id edit?]}]
  (let [step             0.1
        current-unit     (<sub [::sou-toggle/unit-in-selected-system :m2])
        on-change-number (fn [e]
                           (let [v (-> e futil/tval (futil/parse-number step))]
                             (>evt [(if edit? ::edit-occupancy-application-field ::set-new-application-value)
                                    (util/mmap {:attr  :area-to-be-occupied
                                                :value (sou/convert-raw current-unit :metric v)}
                                               app-id ws-id on-change)])))]
    [ui/number-input {:id         "area-to-be-occupied"
                      :class-name class-name
                      :label-text label-text
                      :value      (<sub [::sou-toggle/value-in-selected-system :m2 value])
                      :step       step
                      :on-change  on-change-number
                      :light      true
                      :required   true
                      :unit-label (<sub [::sou-toggle/label-in-selected-system :m2])}]))

(defn date-of-occupancy-input [{:keys [app-id ws-id on-change value class-name label-text edit?]}]
  [ui/date-picker {:id         "date-of-occupancy"
                   :class-name class-name
                   :callback   (fn [val] (>evt [(if edit? ::edit-occupancy-application-field ::set-new-application-value)
                                                (util/mmap {:attr  :expected-date-of-occupancy
                                                            :value val}
                                                           app-id ws-id on-change)]))
                   :label-text label-text
                   :required?  true
                   :value      value}])

(defn description-of-request [{:keys [app-id ws-id value on-change label-text edit?]}]
  [ui/text-area {:label-text label-text
                 :value      value
                 :class      "mt-4"
                 :required   true
                 :on-change  #(>evt [(if edit? ::edit-occupancy-application-field ::set-new-application-value)
                                     (util/mmap {:attr  :description-of-request
                                                 :value (futil/tval %)}
                                                app-id ws-id on-change)])}])

(defn- applicant-select [{:keys [lang tr edit? applicants selected-applicant] :as ctx}]
  (if (> (count applicants) 1)
    [ui-select/select {:id           "partial-occupancy-applicant-select"
                       :label-text   (tr [:role/applicant])
                       :required     true
                       :values       applicants
                       :allow-empty? false
                       :val->str     #(party-model/corporation-and-full-name-or-full-name % lang)
                       :on-change    #(>evt [(if edit? ::edit-occupancy-application-field ::set-new-application-value)
                                             (merge ctx {:attr  :applicant
                                                         :value (:db/id %)})])
                       :value        selected-applicant}]
    [:div
     [:div.bx--label (tr [:role/applicant])]
     [:div.pt-2 (-> (or selected-applicant
                        (first applicants))
                    (party-model/corporation-and-full-name-or-full-name lang))]]))

(defn application-view [{:keys [tr on-change]}]
  (let [lang               (<sub [:language])
        ws-id              (<sub [::ws-subs/workspace-id])
        execution          (<sub [::ws-subs/execution])
        applicants         (<sub [::applicants])
        selected-applicant (p/find-first applicants {:db/id (<sub [::get-new-application-value :applicant ws-id])})]
    [:div.partial-occupancy-application
     (ui/text-to-paragraphs (:execution/partial-occupation-instructions execution) "mb-4")
     [:div.flex.flex-wrap
      [:h4.flex-1 (tr [:occupancy/application])]
      [:div.flex-1.mb-4 [sou-toggle/system-of-units-toggle]]]
     [:div.flex.flex-wrap
      [:div.mt-2.mr-2
       [area-to-be-occupied-input (util/mmap {:label-text (tr [:occupancy/area-to-be-occupied])
                                              :value      (<sub [::get-new-application-value :area-to-be-occupied ws-id])}
                                             ws-id on-change)]]
      [:div.mt-2.mr-4
       [date-of-occupancy-input (util/mmap {:label-text (tr [:occupancy/expected-date-of-occupancy])
                                            :value      (<sub [::get-new-application-value :expected-date-of-occupancy ws-id])}
                                           on-change ws-id)]]
      [:div.mt-2
       [applicant-select (util/mmap lang tr on-change ws-id applicants selected-applicant)]]]
     [:div.flex
      [description-of-request (util/mmap {:label-text (tr [:occupancy/description-of-request])
                                          :value      (<sub [::get-new-application-value :description-of-request ws-id])}
                                         on-change ws-id)]]
     [:div.bg-blue-200.mt-4.p-2
      [:div.font-bold (tr [:occupancy/declaration-of-the-applicant])]
      [:div.mt-2 [:span.font-bold "1. "] (tr [:occupancy/declaration-part-1])]
      [:div.my-2 [:span.font-bold "2. "] (tr [:occupancy/declaration-part-2])]]
     (when selected-applicant
       [:div.mt-4
        [:div.font-bold (tr [:occupancy/applying-on-behalf]
                            [(party-model/corporation-or-full-name selected-applicant lang)])]])]))


(defn partial-occupancy-enable-section [{:keys [execution tr ws-id]} enable-permitted? apply-permitted?]
  (let [original-data                                   (<sub [::data-in-execution])
        editor-data                                     (<sub [::data-in-editor ws-id])
        {:keys [enabled? instructions] :as merged-data} (merge original-data
                                                               editor-data)
        changed?                                        (not= original-data merged-data)]
    (cond
      enable-permitted?
      [:div.px-4.pt-4.pb-2.mb-4.bg-gray-200
       [:div.flex.mb-2.items-center
        [:span.font-bold.mr-4.py-3 (tr [:occupancy/allow-partial-occupancy])]
        (cc/toggle {:id         "partial-occupancy-toggle"
                    :labelA     (tr [:no])
                    :labelB     (tr [:yes])
                    :toggled    enabled?
                    :class-name "unlabeled-toggle"
                    :on-toggle  #(>evt [::toggle-partial-occupancy ws-id %])})]
       (when enabled?
         [ui/text-area {:label-text (tr [:occupancy/instructions-for-applying])
                        :value      instructions
                        :light      true
                        :on-change  #(>evt [::set-partial-occupancy-instructions ws-id (-> % futil/tval)])}])
       [:div.my-2.text-right
        (cc/button
          {:kind       :ghost
           :class-name "mx-2"
           :disabled   (not changed?)
           :on-click   #(>evt [::clear-config-editor ws-id])}
          (tr [:cancel]))
        (cc/button
          {:kind     :primary
           :disabled (or (not changed?)
                         (str/blank? instructions))
           :on-click (fn [_]
                       (>evt [:app.front.process/signal
                              execution
                              [:process.signal/set-partial-occupancy
                               {:enabled?     enabled?
                                :instructions instructions}]
                              {:notify {:success :occupancy/successfully-edited}}]))}
          (tr [:save]))]]

      apply-permitted?
      [:div.mb-6
       [:h3.mb-0 (tr [:occupancy/instructions-for-applying])]
       [:<> (ui/text-to-paragraphs instructions "mt-4")]])))
